import React, { ReactElement } from 'react';
import { useMutation } from 'react-apollo';
import { MetricsTracker, USER_EVENTS } from '@sm/metrics';
import { FormattedDateTime, FormattedRelativeTime } from '@sm/intl';
import { sanitizeString } from '@sm/utils';
import { Immutable, safeRest } from '@wds/utils';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import notificationsMarkAsReadQuery from './NotificationsMarkAsRead.graphql';
import useStyles from './useStylesNotificationItem';

export type NotificationItemProps = Immutable<{
  children: string;
  id: string;
  read?: boolean;
  sentAt: string;
  campaignName: string;
}>;

const NotificationItem = ({
  children,
  read = false,
  sentAt,
  id,
  campaignName,
  ...rest
}: NotificationItemProps): ReactElement => {
  const { notificationsItem } = useStyles({ read });

  const [clickInProductMessage] = useMutation(notificationsMarkAsReadQuery);

  const trackNotificationsEvent = () => {
    MetricsTracker.track({
      name: USER_EVENTS.ELEMENT_INTERACTION,
      data: {
        amplitudeEvent: 'notification center interaction',
        interactionType: 'click',
        interactionLocation: 'mark as read',
        campaignName,
      },
    });
  };

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLAnchorElement;
    if (target.tagName !== 'A') {
      return;
    }
    e.preventDefault();
    const href = target.getAttribute('href');

    clickInProductMessage({
      variables: {
        input: {
          id,
          linkUrl: href,
        },
      },
    });

    trackNotificationsEvent();

    if (href) {
      (window as Window).location = href;
    }
  };

  const date = <FormattedRelativeTime value={new Date(sentAt)} />;
  const time = (
    <FormattedDateTime
      value={new Date(sentAt)}
      hour="numeric"
      minute="numeric"
      hour12
    />
  );

  return (
    <li className={notificationsItem} {...safeRest(rest)}>
      <Box mb={2}>
        <Typography component="p" variant="bodySm" color="darkMuted">
          {date}&nbsp;&nbsp;&nbsp;{time}
        </Typography>
      </Box>
      <Typography weight={!read ? 'medium' : 'regular'}>
        {/* eslint-disable-next-line */}
        <div
          onClick={e => onClick(e)}
          dangerouslySetInnerHTML={{
            __html: sanitizeString(children.toString()),
          }}
        />
      </Typography>
    </li>
  );
};

export default NotificationItem;
export { NotificationItem };
